import type { StripeError } from '@stripe/stripe-js';
import { RESPONSE_FROM_REQUEST_PAGE } from '@/lib/zuora/classifyZuoraError';
import type { ErrorCategory } from './errorCategory';

const STRIPE_AUTHENTICATION_CODES = new Set([
  'authentication_required',
  'setup_intent_authentication_failure',
]);

// Exact-match Stripe error types, keyed to their category. `card_error` is
// handled separately above the lookup: it needs `error.code`, not just
// `error.type`.
const STRIPE_ERROR_CATEGORY_BY_TYPE: Partial<Record<StripeError['type'], ErrorCategory>> = {
  validation_error: 'user_input',
  api_connection_error: 'network',
  rate_limit_error: 'client_error',
  api_error: 'server_error',
  authentication_error: 'server_error',
  invalid_request_error: 'server_error',
  idempotency_error: 'server_error',
};

export const toStripeErrorCategory = (error: StripeError): ErrorCategory => {
  if (error.type === 'card_error') {
    return error.code !== undefined && STRIPE_AUTHENTICATION_CODES.has(error.code)
      ? 'authentication'
      : 'declined';
  }

  return STRIPE_ERROR_CATEGORY_BY_TYPE[error.type] ?? 'unknown';
};

export type ZuoraErrorCategoryInput = {
  /** Zuora `errorCode`, or `null` for a synthetic load/render timeout. */
  code: string | null;
  /** Zuora `responseFrom` — a request-page response means the form never rendered. */
  responseFrom?: string;
};

// Exact-match Zuora error codes, keyed to their category.
//
// Mirrors SYSTEM_CODES in lib/zuora/classifyZuoraError.ts. Re-declared rather
// than imported: that module owns banner copy and log severity, this one owns
// reporting segmentation, and a change to one must not move the other.
const ZUORA_ERROR_CATEGORY_BY_CODE: Record<string, ErrorCategory> = {
  BusinessValidationError: 'user_input',
  GatewayTransactionError: 'declined',
  ThreeDs2_Authentication_Exception: 'authentication',
  Invalid_Security: 'session_expired',
  Attempt_Exceed_Limitation: 'session_expired',
  ReCaptcha_Validation_Failed: 'interrupted',
  Submit_Too_Quick: 'interrupted',
  GeneralSystemError: 'server_error',
  Decryption_Error: 'server_error',
  Invalid_PaymentGateway: 'server_error',
  Invalid_Request_Method: 'server_error',
  Validate_Dynamic_Params_Failed: 'server_error',
};

export const toZuoraErrorCategory = ({
  code,
  responseFrom,
}: ZuoraErrorCategoryInput): ErrorCategory => {
  // Neither got as far as a submission, so this is a form-load failure rather
  // than anything the payment itself did.
  if (code === null || responseFrom === RESPONSE_FROM_REQUEST_PAGE) return 'form_load';

  return ZUORA_ERROR_CATEGORY_BY_CODE[code] ?? 'unknown';
};
